import {useNavigate} from 'react-router'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import sortBy from 'lodash/sortBy'
import {SUPPORTED_MATH_GRADES} from '@freckle/educator-entities/ts/common/helpers/constants'
import {toFragment} from '@freckle/educator-entities/ts/common/helpers/routers/query-params'
import {createSelectorOptionAsItem} from '@freckle/educator-entities/ts/common/logic/generic-selector'
import type {RlDomainT} from '@freckle/educator-entities/ts/common/models/rl-domain'
import {domainHasGrade} from '@freckle/educator-entities/ts/common/models/rl-domain'
import type {RlStandardSetT} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import type {RlDomainIdT} from '@freckle/educator-entities/ts/common/types/rl-domain-id'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {H1} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import type {IdentityProviderOrNoneT} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import ProficiencyReportFilters from '@freckle/school/ts/proficiency-report/components/school/proficiency-report-filters'
import {getProficiencyReportTableDataFromCollection} from '@freckle/school/ts/proficiency-report/logic/proficiency-report-table-logic'
import type {ProficiencyReportAttrs} from '@freckle/school/ts/proficiency-report/models/proficiency-report'
import type {StudentsMapT} from '@freckle/school/ts/users/collections/students'
import ProficiencyReportTable from './table'

type Props = {
  students: StudentsMapT
  teachers: Array<BaseTeacherAttrs>
  schools: Array<SchoolAttrs>
  domains: Array<RlDomainT>
  domainGradeCoverage: Set<number>
  selectedSchoolsIds: Array<number>
  paramsSchoolsIds: Array<number>
  selectedDomainId: RlDomainIdT
  selectedGrade: number
  selectedIdentityProviders: Array<IdentityProviderOrNoneT>
  proficiencyReportData: Array<ProficiencyReportAttrs>
  mathStandardSet: RlStandardSetT
  summary: SummaryT
}

export const SchoolProficiencyReport = (props: Props) => {
  const {
    students,
    schools,
    teachers,
    domains,
    domainGradeCoverage,
    mathStandardSet,
    proficiencyReportData,
    selectedDomainId,
    selectedSchoolsIds,
    paramsSchoolsIds,
    selectedGrade,
    selectedIdentityProviders,
    summary
  } = props

  const {t} = useSafeTranslation()
  const navigate = useNavigate()
  const onParamChange = (
    domainId: RlDomainIdT,
    grade: number,
    schoolsIds: Array<number>,
    identityProviders: Array<IdentityProviderOrNoneT>
  ) => {
    const schoolsIdsParam = isEmpty(schoolsIds) ? {} : {schoolsIds}
    const params = {
      domainId,
      grade,
      identityProviders,
      ...schoolsIdsParam
    }

    const url = toFragment(Routes.schoolProficiencyReport(), params)
    navigate(url)
  }

  const onSelectDomain = (domainId: RlDomainIdT) =>
    onParamChange(domainId, selectedGrade, selectedSchoolsIds, selectedIdentityProviders)

  const onSelectGrade = (grade: number) =>
    onParamChange(selectedDomainId, grade, selectedSchoolsIds, selectedIdentityProviders)

  const onToggleSchoolSelection = (schools: SchoolAttrs[]) => {
    const newSchoolsIds = schools.map(school => school.id)

    onParamChange(selectedDomainId, selectedGrade, newSchoolsIds, selectedIdentityProviders)
  }

  const isSchoolSelected = (school: SchoolAttrs): boolean =>
    find(selectedSchoolsIds, id => id === school.id) !== undefined

  const onSelectIdentityProviders = (idps: Array<IdentityProviderOrNoneT>) =>
    onParamChange(selectedDomainId, selectedGrade, selectedSchoolsIds, idps)

  // Here we should only display relevant domains given the grade level
  // currently selected.
  const relevantDomains = domains.filter(domain => domainHasGrade(domain, selectedGrade))
  const domainOptions = relevantDomains.map(dom => createSelectorOptionAsItem(dom.id, dom.name))

  const possibleGrades = SUPPORTED_MATH_GRADES.filter(g => domainGradeCoverage.has(g))

  const gradeOptions = possibleGrades.map(grade =>
    createSelectorOptionAsItem(grade, t('DISPLAY_GRADE_AS_WORDS', {grade, capitalize: true}))
  )

  const schoolsOptions = new Set(sortBy(schools, school => school.name))

  const tableData = getProficiencyReportTableDataFromCollection(proficiencyReportData, schools)

  return (
    <div className="proficiency-report-container">
      <H1>{t('SCHOOL_PROFICIENCY_REPORT_H1_TITLE')}</H1>
      <div className="alert alert-info">
        <p>{t('SCHOOL_PROFICIENCY_REPORT_P_DESCRIPTION')}</p>
      </div>
      <ProficiencyReportFilters
        onSelectDomain={onSelectDomain}
        selectedDomainId={selectedDomainId}
        domainOptions={domainOptions}
        onToggleSchoolSelection={onToggleSchoolSelection}
        isSchoolSelected={isSchoolSelected}
        selectedSchoolsIds={selectedSchoolsIds}
        paramsSchoolsIds={paramsSchoolsIds}
        schoolOptions={schoolsOptions}
        onSelectGrade={onSelectGrade}
        selectedGrade={selectedGrade}
        gradeOptions={gradeOptions}
        selectedIdentityProviders={selectedIdentityProviders}
        onSelectIdentityProviders={onSelectIdentityProviders}
        summary={summary}
      />
      <ProficiencyReportTable
        tableData={tableData}
        students={students}
        schools={schools}
        teachers={teachers}
        selectedDomainId={selectedDomainId}
        selectedGrade={selectedGrade}
        mathStandardSet={mathStandardSet}
      />
    </div>
  )
}
